import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Dropdown, ListGroup, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { isIterableArray } from '../utils/utils';
import FalconCardHeader from './common/FalconCardHeader';
import Notification from './notification/Notification';
import NotificationBanner from './notification/NotificationBanner';
import { useNotificationContext } from '../providers/NotificationProvider';
import { useSetAllNewNotificationToReadMutation } from '../slices/notificationsApiSlice';

const MAX_NOTIFICATIONS_DISPLAY = 7;
const BANNER_SHOWN_IDS_KEY = 'notificationBannerShownIds';

const readShownBannerIds = () => {
	try {
		const parsed = JSON.parse(sessionStorage.getItem(BANNER_SHOWN_IDS_KEY) || '[]');
		return new Set(Array.isArray(parsed) ? parsed : []);
	} catch {
		return new Set();
	}
};

const markBannerShown = (id) => {
	if (!id) return;
	const ids = readShownBannerIds();
	ids.add(id);
	sessionStorage.setItem(BANNER_SHOWN_IDS_KEY, JSON.stringify([...ids]));
};

const NotificationDropdown = ({ variant = 'desktop' }) => {
	const isMobile = variant === 'mobile';
	const { newUnreadNotifications = [], refetch, setToRead, loadingNotify, notificationsReady } = useNotificationContext();
	const [setAllNewUnreadNotifications, { isLoading: isMarkingAllRead }] = useSetAllNewNotificationToReadMutation();

	const [isOpen, setIsOpen] = useState(false);
	const [bannerNotification, setBannerNotification] = useState(null);
	const anchorRef = useRef(null);

	const latestUnread = useMemo(() => newUnreadNotifications.find((notification) => notification.unread === true) ?? null, [newUnreadNotifications]);

	useEffect(() => {
		if (!notificationsReady || isOpen || bannerNotification) return undefined;
		const unread = latestUnread;
		if (!unread?._id || readShownBannerIds().has(unread._id)) return undefined;
		const timer = window.setTimeout(() => {
			if (readShownBannerIds().has(unread._id)) return;
			markBannerShown(unread._id);
			setBannerNotification(unread);
		}, 0);
		return () => window.clearTimeout(timer);
	}, [notificationsReady, latestUnread, isOpen, bannerNotification]);

	// 计算是否有未读通知
	const hasUnreadNotifications = useMemo(() => {
		return newUnreadNotifications.some((notification) => notification.unread === true);
	}, [newUnreadNotifications]);

	// 限制显示的通知数量
	const displayedNotifications = useMemo(() => {
		return newUnreadNotifications.slice(0, MAX_NOTIFICATIONS_DISPLAY);
	}, [newUnreadNotifications]);

	// 处理下拉菜单切换
	const handleDropdownToggle = useCallback(
		(isOpenState) => {
			setIsOpen(isOpenState);
			// 只在打开时刷新数据，避免不必要的请求
			if (isOpenState) {
				refetch();
			}
		},
		[refetch],
	);

	// 处理单个通知点击
	const handleNotificationClick = useCallback(
		async (notification) => {
			// 如果通知未读，标记为已读
			if (notification.unread) {
				try {
					await setToRead(notification).unwrap();
					// 使用 refetch 来更新列表，但不需要等待
					refetch();
				} catch (error) {
					// 标记通知为已读失败，静默处理
				}
			}
		},
		[setToRead, refetch],
	);

	// 标记全部为已读
	const handleMarkAllAsRead = useCallback(
		async (e) => {
			e.preventDefault();
			e.stopPropagation();

			// 只处理未读通知
			const unreadNotifications = newUnreadNotifications.filter((notification) => notification.unread === true);

			if (unreadNotifications.length === 0) {
				return;
			}

			try {
				const updatedNotifications = unreadNotifications.map((notification) => ({
					...notification,
					unread: false,
				}));
				await setAllNewUnreadNotifications(updatedNotifications).unwrap();
				refetch();
			} catch (error) {
				// 标记全部已读失败，静默处理
			}
		},
		[newUnreadNotifications, setAllNewUnreadNotifications, refetch],
	);

	// 处理查看全部点击
	const handleViewAllClick = useCallback((e) => {
		e.preventDefault();
		setIsOpen(false);
	}, []);

	const handleBannerClosed = useCallback(() => {
		setBannerNotification(null);
	}, []);

	const dropdownMenu = (
		<Dropdown.Menu className='dropdown-menu-card dropdown-menu-end dropdown-caret dropdown-caret-bg'>
			<Card className='dropdown-menu-notification dropdown-menu-end shadow-none' style={{ maxWidth: '20rem' }}>
				<FalconCardHeader
					className='card-header'
					title='通知📢'
					titleTag='h6'
					light={false}
					endEl={
						hasUnreadNotifications ? (
							<Link
								className='card-link fw-normal'
								to='#!'
								onClick={handleMarkAllAsRead}
								style={{ pointerEvents: isMarkingAllRead ? 'none' : 'auto', opacity: isMarkingAllRead ? 0.6 : 1 }}
							>
								{isMarkingAllRead ? (
									<>
										<Spinner size='sm' className='me-1' />
										处理中
									</>
								) : (
									'标记全部已读'
								)}
							</Link>
						) : null
					}
				/>
				<ListGroup variant='flush' className='fw-normal fs-10 scrollbar' style={{ maxHeight: '19rem' }}>
					<div className='list-group-title'>最新</div>
					{loadingNotify ? (
						<ListGroup.Item>
							<div className='notification-body text-center py-4'>
								<Spinner size='sm' className='me-2' />
								<span>加载中...</span>
							</div>
						</ListGroup.Item>
					) : displayedNotifications.length === 0 ? (
						<ListGroup.Item>
							<div className='notification-body text-center'>
								<FontAwesomeIcon icon='fa-brands fa-watchman-monitoring' className='fs-9 mb-2 mt-5 text-muted' />
								<p className='mb-5 text-muted'>无任何通知</p>
							</div>
						</ListGroup.Item>
					) : (
						isIterableArray(displayedNotifications) &&
						displayedNotifications.map((notification) => (
							<ListGroup.Item
								key={notification._id}
								onClick={() => handleNotificationClick(notification)}
								className={classNames({ 'bg-body-tertiary': notification.unread })}
								style={{ cursor: 'pointer' }}
							>
								<Notification {...notification} flush />
							</ListGroup.Item>
						))
					)}
				</ListGroup>
				{displayedNotifications.length > 0 && (
					<div className='card-footer text-center border-top' onClick={handleViewAllClick}>
						<Link className='card-link d-block' to='/notificationlist'>
							查看全部
						</Link>
					</div>
				)}
			</Card>
		</Dropdown.Menu>
	);

	if (isMobile) {
		return (
			<>
				<Dropdown align='end' show={isOpen} onToggle={handleDropdownToggle} className='navbar-mobile-notification'>
					<Dropdown.Toggle
						ref={anchorRef}
						as={Button}
						variant='link'
						className={classNames('navbar-mobile-icon-btn position-relative', {
							'notification-indicator notification-indicator-danger notification-indicator-fill': hasUnreadNotifications,
						})}
						aria-label={hasUnreadNotifications ? '您有未读通知' : '通知'}
					>
						<FontAwesomeIcon icon='bell' className='navbar-mobile-icon-btn__glyph' />
					</Dropdown.Toggle>
					{dropdownMenu}
				</Dropdown>
				{bannerNotification ? (
					<NotificationBanner
						notification={bannerNotification}
						anchorRef={anchorRef}
						forceClose={isOpen}
						onActivate={handleNotificationClick}
						onClosed={handleBannerClosed}
					/>
				) : null}
			</>
		);
	}

	return (
		<>
			<Dropdown navbar={true} as='li' show={isOpen} onToggle={handleDropdownToggle}>
				<Dropdown.Toggle
					ref={anchorRef}
					bsPrefix='toggle'
					as={Link}
					to='#!'
					className={classNames('px-0 nav-link', {
						'notification-indicator notification-indicator-primary': hasUnreadNotifications,
					})}
					title={hasUnreadNotifications ? '您有未读通知' : '通知'}
				>
					<FontAwesomeIcon icon='bell' transform='shrink-6' className='fs-5' />
				</Dropdown.Toggle>
				{dropdownMenu}
			</Dropdown>
			{bannerNotification ? (
				<NotificationBanner
					notification={bannerNotification}
					anchorRef={anchorRef}
					forceClose={isOpen}
					onActivate={handleNotificationClick}
					onClosed={handleBannerClosed}
				/>
			) : null}
		</>
	);
};

export default NotificationDropdown;
